import React, { useState } from 'react';
import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import toPairs from 'lodash/toPairs';
import classnames from 'classnames';
import moment from 'moment';
import { DraftBox } from '../DraftBox/DraftBox';
import { Input } from '../../../Form/Input';
import { Button } from '../../../Button';
import validationSchema from './validationSchema';
import {
  getReducedDrafts,
  americanDateToGlobal,
} from '../../../../shared/helpers';

const DraftForm = ({ drafts, onSubmit, handleCloseModal }) => {
  const [internalDrafts, setDrafts] = useState(getReducedDrafts(drafts));

  const hasPreviousUrlRemoved = (index, draftsList) =>
    draftsList.filter((d, i) => !d.url && i < index).length;

  const handleSubmit = (values, { setSubmitting }) => {
    const draftsList = toPairs(values).map(([key, val]) => ({
      ...val,
      name: key,
    }));

    const draftsMap = draftsList.map((d, index) => {
      const draft = {
        ...d,
        due_date: americanDateToGlobal(d.due_date),
      };

      const prevDraft = draftsList[index - 1];

      if (hasPreviousUrlRemoved(index, draftsList)) {
        return {
          ...draft,
          url: '',
          status: draft.due_date ? 'due_in' : 'not_started',
        };
      }

      if (draft.url && draft.due_date) {
        return { ...draft, status: 'submitted' };
      }

      if (draft.due_date) {
        return { ...draft, status: 'due_in' };
      }

      if (prevDraft && !prevDraft.url && !draft.due_date) {
        return { ...draft, status: 'not_started' };
      }

      if (prevDraft && prevDraft.url && !draft.due_date) {
        return { ...draft, status: 'in_progress' };
      }

      if (!prevDraft && !draft.url && !draft.due_date) {
        return { ...draft, status: 'in_progress' };
      }

      return draft;
    });

    onSubmit(draftsMap, value => {
      setSubmitting(value);
      handleCloseModal();
    });
  };

  const handleDateChange = (e, draft, setFieldValue) => {
    const { value } = e.target;

    const draftsToState = {
      ...internalDrafts,
      [draft.name]: {
        ...draft,
        due_date: value,
      },
    };
    setDrafts(draftsToState);
    setFieldValue(`${draft.name}.due_date`, value);
  };

  const handleUrlChange = (e, draft, setFieldValue) => {
    const { value } = e.target;

    const draftsToState = {
      ...internalDrafts,
      [draft.name]: {
        ...draft,
        url: value,
      },
    };
    setDrafts(draftsToState);
    setFieldValue(`${draft.name}.url`, value);
  };

  const isDaftEnabled = (index, draftsArr) => {
    const localDrafts = draftsArr.map(d => d[1]);

    let lastSubmittedIndex = -1;

    localDrafts.forEach((d, i) => {
      if (d.status === 'submitted') {
        lastSubmittedIndex = i;
      }
    });

    return index === lastSubmittedIndex + 1 || index === lastSubmittedIndex;
  };

  const className = (draft, disabled) => disabled && draft.status !== "submitted" ? "disabled" : draft.status
  const placeholders = {
    "draft_1": "First Draft",
    "draft_2": "Second Draft",
    "draft_3": "Third Draft",
    "draft_4": "Fourth Draft"
  }

  const getLabel = (status, info = '') => {
    const labels = {
      not_started: () => `Not Started`,
      submitted: ({ updated_at: date }) =>
        `Submitted ${moment(date, 'YYYY-MM-DD').format('MM/DD/YYYY')}`,
      due_in: ({ due_date: date }) => {
        const days = moment(date, 'MM/DD/YYYY').diff(moment(), 'days') + 1;

        if (Number.isNaN(days)) {
          return 'In Progress';
        }

        return days < 0 ? (
          <span className="font-weight-bold text-danger">{`Late ${days *
            -1} days`}</span>
        ) : (
          `Due in ${days} days`
        );
      },
      in_progress: () => 'In Progress',
    };

    return labels[status](info);
  };

  return (
    <Formik
      initialValues={internalDrafts}
      onSubmit={handleSubmit}
      validateOnBlur
      validateOnChange
      isInitialValid
      enableReinitialize
      validationSchema={validationSchema(drafts, internalDrafts)}
      displayName="DraftForm"
    >
      {({ values, isSubmitting, isValid, setFieldValue }) => (
        <Form className="col-12">
          <div className="row mb-0">
            <div className="col-12 col-md offset-md-3 d-flex">
              <div className="row d-none d-md-flex align-self-stretch flex-fill">
                <div className="col-4 d-flex">
                  <span className="text-gray-2 t-14 font-weight-mid">
                    Estimated Schedule
                  </span>
                </div>
                <div className="col-8 d-flex">
                  <span className="text-gray-2 t-14 font-weight-mid">
                    Video Link
                  </span>
                </div>
              </div>
            </div>
          </div>
          {Object.entries(values).map(([, value], index, array) => (
            <div className="row mb-4 draft-box-row" key={String(index)}>
              <div className="col-12 col-md-3">
                <div className="row mb-1 mb-md-0">
                  <div
                    className={classnames(
                      "col-12",
                      className(value, !isDaftEnabled(index, array)),
                    )}
                  >
                    <div className="row">
                      <span className="col-12">
                        {placeholders[value.name]}
                      </span>
                      <br/>
                      <span className="col-12 t-12">
                        {getLabel(value.status, value)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-12 col-md d-flex">
                <div className="row align-self-stretch flex-fill">
                  <div className="col-12 col-md-4 d-flex">
                    <div className="row align-self-stretch flex-fill d-flex align-items-center">
                      <Input
                        containerClassName="mb-0"
                        inputClassName="my-2 bg-transparent"
                        type="dateInput"
                        fluid
                        name={`${value.name}.due_date`}
                        placeholder="MM/DD/YYYY"
                        onChange={e =>
                          handleDateChange(e, value, setFieldValue)
                        }
                        value={value.due_date}
                      />
                    </div>
                  </div>
                  <div className="col-12 col-md-8 d-flex">
                    <div className="row align-self-stretch flex-fill d-flex align-items-center">
                      <Input
                        containerClassName="mb-0"
                        inputClassName="my-2 bg-transparent"
                        type="text"
                        fluid
                        name={`${value.name}.url`}
                        onChange={e => handleUrlChange(e, value, setFieldValue)}
                        disabled={!isDaftEnabled(index, array)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}

          <div className="row">
            <div className="mt-4 col col-md-4 offset-md-4 text-center">
              <Button
                className="btn-primary btn-md-inline px-4 text-uppercase"
                block
                rounded
                type="submit"
                title="Save"
                disabled={isSubmitting || !isValid}
              />
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};

DraftForm.propTypes = {
  drafts: PropTypes.array,
  onSubmit: PropTypes.func,
  handleCloseModal: PropTypes.func,
};

export default DraftForm;



// WEBPACK FOOTER //
// ./src/components/ProjectView/DraftModal/DraftForm/DraftForm.js